import { useState, useMemo, memo, useCallback, useEffect, useRef } from 'react';
import { NavLink, generatePath } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { TooltipControl } from 'src/components/base/Tooltip';
import { MapView, Marker, MarkerClusterer, InfoWindow, MarkerSymbols, withLoadedMap } from 'src/components/base/MapView';
import { useModalDialog, ConfirmModalDialog } from 'src/components/base/ModalDialog';
import { isValidPosition } from 'src/utils/helpers';
import { EventPriority } from 'src/components/Trap/Elements';

import classnames from './MapPanel.module.scss';

export const MapPanel = withLoadedMap(({ items, editMode, selectedItem, onSelectItem, onCenterChanged, onRemoveFromMap, onItemPositionChange, canEditItem, dataLoaded = false }) => {
  const mapRef = useRef();
  const [selected, setSelected] = useState();
  const [draggable, setDraggable] = useState();
  const [mapReady, setMapReady] = useState(false);
  const [hasCustomViewport, setHasCustomViewport] = useState(false);
  const autoFitInProgressRef = useRef(false);
  const initialFitDoneRef = useRef(false);
  const isMarkerDraggingRef = useRef(false);
  const draggedMarkerOriginalPositionRef = useRef(null);

  const clustersVersion = useRef(0);
  const getClusterKey = useCallback(({ lat, lng }) => `${lat.toFixed(5)}/${lng.toFixed(5)}`, []);
  const visibleItems = useMemo(() => items, [items]);
  const clusters = useMemo(() => visibleItems.reduce((acc, item) => {
    if (isValidPosition(item.changed)) {
      const key = getClusterKey(item.changed);
      const bucket = acc.get(key) || (acc.set(key, []), acc.get(key));
      bucket.push(item);
    }
    return acc;
  }, new Map()), [visibleItems, clustersVersion.current, getClusterKey]);

  const { markers, bounds } = useMemo(() => {
    const bounds = new google.maps.LatLngBounds();
    const markers = visibleItems.reduce((acc, item) => {
      if (isValidPosition(item.changed)) {
        bounds.extend(item.changed);
        acc.push(item);
      }
      return acc;
    }, []);
    return { markers, bounds };
  }, [visibleItems]);

  // Track markers count to detect when new markers are added
  const previousMarkersCountRef = useRef(0);

  useEffect(() => {
    // Wait for map ready, data loaded, markers exist, and not in edit mode
    // Also skip if a marker is currently being dragged
    if (!mapReady || !dataLoaded || markers.length === 0 || editMode || isMarkerDraggingRef.current) { 
      return; 
    }
    
    const map = mapRef.current;
    if (!map) { return; }

    // Recalculate bounds from current markers to ensure we have the latest
    // If a marker is being dragged, use its original position instead of current position
    const currentBounds = new google.maps.LatLngBounds();
    markers.forEach(marker => {
      if (isValidPosition(marker.changed)) {
        // If this marker is being dragged, use original position for bounds calculation
        const position = (isMarkerDraggingRef.current && draggedMarkerOriginalPositionRef.current && 
                         marker === draggedMarkerOriginalPositionRef.current.marker) 
          ? draggedMarkerOriginalPositionRef.current.originalPosition 
          : marker.changed;
        if (isValidPosition(position)) {
          currentBounds.extend(position);
        }
      }
    });

    // Check if bounds is valid (has at least one distinct point)
    try {
      const ne = currentBounds.getNorthEast();
      const sw = currentBounds.getSouthWest();
      if (!ne || !sw || ne.equals(sw)) { 
        return; 
      }
    } catch (e) {
      // Bounds might not be initialized yet
      return;
    }

    // Check if markers have been added (new markers appeared)
    const markersCountChanged = previousMarkersCountRef.current !== markers.length;
    
    // Determine if we should auto-fit:
    // 1. First time (initial fit not done) - always auto-fit regardless of hasCustomViewport
    // 2. New markers were added (marker count changed)
    const isInitialFit = !initialFitDoneRef.current;
    const shouldAutoFit = isInitialFit || markersCountChanged;
    
    if (!shouldAutoFit) {
      return;
    }

    // If user has manually changed viewport and no new markers were added, don't auto-fit
    // But always allow initial fit even if hasCustomViewport is somehow set
    if (!isInitialFit && !markersCountChanged && hasCustomViewport) {
      return;
    }

    // Update refs before fitting
    initialFitDoneRef.current = true;
    previousMarkersCountRef.current = markers.length;
    autoFitInProgressRef.current = true;
    
    // Fit bounds with padding for better visual appearance
    try {
      map.fitBounds(currentBounds, {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50
      });

      const listener = google.maps.event.addListenerOnce(map, 'idle', () => {
        autoFitInProgressRef.current = false;
      });

      return () => {
        if (listener) {
          google.maps.event.removeListener(listener);
        }
        autoFitInProgressRef.current = false;
      };
    } catch (e) {
      // If fitBounds fails, reset flag
      autoFitInProgressRef.current = false;
    }
  }, [mapReady, dataLoaded, markers, editMode, hasCustomViewport]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) { return }
    
    // Don't reposition map if a marker is being dragged
    if (isMarkerDraggingRef.current) { return }

    if (selectedItem && isValidPosition(selectedItem.changed)) {
      if (!selected || selected[0] !== selectedItem) {
        setSelected([selectedItem]);
        map.setCenter(selectedItem.changed);
      }
    }
    else {
      setSelected(null);
    }
  }, [mapReady, selectedItem, selected]);

  const handleCenterChanged = useCallback(() => {
    const map = mapRef.current;
    if (!map || !onCenterChanged) { return }

    const center = map.getCenter();
    onCenterChanged({ lat: center.lat(), lng: center.lng() });
  }, [mapRef, onCenterChanged]);

  const handleZoomChanged = useCallback(() => {
    if (autoFitInProgressRef.current) {
      autoFitInProgressRef.current = false;
      return;
    }
    setHasCustomViewport(true);
  }, []);

  const handleDragEnd = useCallback(() => {
    if (autoFitInProgressRef.current) {
      return;
    }
    setHasCustomViewport(true);
  }, []);

  const handleMarkerDragStart = useCallback((item, position) => {
    if (typeof canEditItem === 'function' && !canEditItem(item)) { return; }
    // Store original position before dragging starts
    draggedMarkerOriginalPositionRef.current = {
      marker: item,
      originalPosition: item.changed ? { ...item.changed } : null
    };
    isMarkerDraggingRef.current = true;
    // Mark viewport as custom to prevent auto-fit after drag
    setHasCustomViewport(true);
    item.changed = position;
    setSelected([item]);
    setDraggable(position);
  }, [canEditItem])

  const handleMarkerDrag = useCallback((item, position) => {
    if (typeof canEditItem === 'function' && !canEditItem(item)) { return; }
    item.changed = position;
    setSelected([item]);
    setDraggable(position);
  }, [canEditItem])

  const handleMarkerDragEnd = useCallback((item, position) => {
    if (typeof canEditItem === 'function' && !canEditItem(item)) { return; }
    item.changed = position;
    clustersVersion.current++;
    setDraggable(null);
    isMarkerDraggingRef.current = false;
    draggedMarkerOriginalPositionRef.current = null;
    if (typeof onItemPositionChange === 'function') {
      onItemPositionChange(item);
    }
  }, [canEditItem, onItemPositionChange])

  const handleMarkerClick = useCallback((item) => {
    const group = clusters.get(getClusterKey(item.changed));
    setSelected(group || [item]);
    onSelectItem(item);
  }, [clusters, getClusterKey, onSelectItem])

  const handleCloseInfo = useCallback(() => {
    setSelected(null);
    onSelectItem && onSelectItem(null);
  }, [onSelectItem])

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && selectedItem) {
        onSelectItem && onSelectItem(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedItem, onSelectItem]);

  return (
    <div className="w-100 h100 position-relative">
  <MapView
        ref={mapRef}
        zoom={8}
        onCenterChanged={handleCenterChanged}
        onZoomChanged={handleZoomChanged}
        onDragEnd={handleDragEnd}
        onMapReady={() => {
          setMapReady(true);
          autoFitInProgressRef.current = false;
          // Ensure parent receives initial center immediately so dropping markers works before any zoom/drag
          handleCenterChanged();
        }}
        onMapUnmount={() => {
          setMapReady(false);
          setHasCustomViewport(false);
          autoFitInProgressRef.current = false;
          initialFitDoneRef.current = false;
          previousMarkersCountRef.current = 0;
          isMarkerDraggingRef.current = false;
          draggedMarkerOriginalPositionRef.current = null;
        }}
      >
        {selected && <ItemsInfo items={selected} editMode={editMode} onRemoveFromMap={onRemoveFromMap} onClose={handleCloseInfo} canEditItem={canEditItem} />}

        <ItemClusterer markers={markers} editMode={editMode} canEditItem={canEditItem}
          onMarkerDragStart={handleMarkerDragStart} onMarkerDrag={handleMarkerDrag}
          onMarkerDragEnd={handleMarkerDragEnd} onMarkerClick={handleMarkerClick}
        />
      </MapView>

      {draggable && <LocationInfo position={draggable} />}
    </div>
  )
})

const ItemClusterer = memo(({ markers, editMode, canEditItem, onMarkerDragStart, onMarkerDrag, onMarkerDragEnd, onMarkerClick }) => {
  const GROUP_MARKER_SYMBOL = {
    // Bootstrap Icons: geo-alt-fill
    path: 'M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z',
    fillColor: '#343a40',
    fillOpacity: 1,
    strokeWeight: 0,
    scale: 1.8,
    anchor: new google.maps.Point(8, 16),
  };

  return <MarkerClusterer averageCenter enableRetinaIcons gridSize={50} maxZoom={18} zoomOnClick={true}>
    {
      (clusterer) => markers.map((item) => {
        let markerIcon;
        if (item.type === 'group') {
          markerIcon = GROUP_MARKER_SYMBOL;
        } else {
          // Check for no_communication first (event_name takes precedence)
          if (item.event_name === 'no_communication') {
            markerIcon = MarkerSymbols.no_comm;
          } else {
            // Check for caught state (is_caught adds '_catch' suffix)
            const priorityKey = item.is_caught
              ? `${item.event_priority}_catch`
              : item.event_priority;
            markerIcon = MarkerSymbols[priorityKey] || MarkerSymbols[item.markerPriority ?? 'normal'] || MarkerSymbols.normal;
          }
        }

        // Create a new position object to ensure Marker updates when position changes
        const markerPosition = item.changed ? { lat: item.changed.lat, lng: item.changed.lng } : null;

        // Include position in key to force Marker re-render when position changes
        const positionKey = markerPosition
          ? `${markerPosition.lat.toFixed(5)}-${markerPosition.lng.toFixed(5)}`
          : 'no-position';
        const markerKey = item.type === 'group'
          ? `${item.markerKey || item.group_id}-${positionKey}`
          : `trap-${item.trap_id}-${positionKey}`;

        return (
          <Marker
            key={markerKey}
            item={item}
            clusterer={clusterer}
            position={markerPosition}
            icon={markerIcon}
            onDragStart={e => onMarkerDragStart(item, { lat: e.latLng.lat(), lng: e.latLng.lng() })}
            onDrag={e => onMarkerDrag(item, { lat: e.latLng.lat(), lng: e.latLng.lng() })}
            onDragEnd={e => onMarkerDragEnd(item, { lat: e.latLng.lat(), lng: e.latLng.lng() })}
            onClick={() => onMarkerClick(item)}
            draggable={editMode && (typeof canEditItem !== 'function' || canEditItem(item))}
          />
        );
      })
    }
  </MarkerClusterer>
})

const ItemsInfo = ({ items, editMode, onRemoveFromMap, onClose, canEditItem }) => {
  const removeModal = useModalDialog();
  const groupItems = items.filter(item => item.type === 'group');
  const trapItems = items.filter(item => item.type === 'trap');
  const isGroupOnly = groupItems.length > 0 && trapItems.length === 0;
  const infoPixelOffset = useMemo(() => new google.maps.Size(0, isGroupOnly ? -30 : -8), [isGroupOnly]);

  const handleRemove = useCallback(async (item, event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!onRemoveFromMap) { return; }

    const label = item.type === 'trap'
      ? (item.trap_name || __('this trap'))
      : (item.group_name || __('this group'));
    const html = __('Remove ${label} from the map? No other data will be lost.').replace('${label}', `<b>${label}</b>`);

    const result = await removeModal.show({
      title: __('Remove from map'),
      actionTitle: __('Remove'),
      variant: 'danger',
      html,
    });

    if (result) {
      await onRemoveFromMap(item);
      onClose();
    }
  }, [onRemoveFromMap, onClose, removeModal]);

  return (
    <InfoWindow onCloseClick={onClose}
      position={items[0].changed} options={{ pixelOffset: infoPixelOffset }}
    >
      <div>
        {groupItems.length > 0 && (
          <div className="mb-2">
            {trapItems.length > 0 && <div className="fw-bold mb-1">{__('Groups')}</div>}
            <table>
              <tbody>
                {groupItems.map((item) => {
                  const { group_id, group_name } = item;
                  const key = item.markerKey || group_id;
                  const link = generatePath('/traps/:id', { id: group_id });

                  return (
                    <tr key={key}>
                      <td>
                        <div className="d-flex align-items-center gap-col-2">
                          <NavLink to={link}>{group_name}</NavLink>
                          {editMode && (!canEditItem || canEditItem(item)) && (
                            <TooltipControl tooltip={__('Remove from map')} placement="top">
                              <Button
                                variant="link"
                                size="sm"
                                className="text-danger p-0 d-inline-flex align-items-center justify-content-center lh-1"
                                onClick={(event) => handleRemove(item, event)}
                              >
                                <i className="bi bi-x-circle" aria-hidden="true" />
                                <span className="visually-hidden">{__('Remove from map')}</span>
                              </Button>
                            </TooltipControl>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {trapItems.length > 0 && (
          <div>
            {groupItems.length > 0 && <div className="fw-bold mb-1">{__('Traps')}</div>}
            <table>
              <tbody>
                {trapItems.map((item) => {
                  const { trap_id, trap_name, event_priority, is_caught, event_name } = item;
                  return (
                    <tr key={trap_id}>
                      <td style={{ width: '16px' }}>
                        <EventPriority
                          priority={event_priority}
                          isCaught={is_caught}
                          eventName={event_name}
                        />
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-col-2">
                          <NavLink to={generatePath('/trap/:trap_id', { trap_id })}>{trap_name}</NavLink>
                          {editMode && (!canEditItem || canEditItem(item)) && (
                            <TooltipControl tooltip={__('Remove from map')} placement="top">
                              <Button
                                variant="link"
                                size="sm"
                                className="text-danger p-0 d-inline-flex align-items-center justify-content-center lh-1"
                                onClick={(event) => handleRemove(item, event)}
                              >
                                <i className="bi bi-x-circle" aria-hidden="true" />
                                <span className="visually-hidden">{__('Remove from map')}</span>
                              </Button>
                            </TooltipControl>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <ConfirmModalDialog {...removeModal.register()} />
      </div>
    </InfoWindow>
  )
}

const LocationInfo = ({ position }) => (
  <div className={classnames.locationInfo}>
    <div className="d-flex flex-column justify-content-center align-items-end">
      <span>{`${__('Latitude')}: ${position.lat.toFixed(5)}`}</span>
      <span>{`${__('Longitude')}: ${position.lng.toFixed(5)}`}</span>
    </div>
  </div>
)
